// Session BDG2: reopen an APPROVED job (admin only). Opens on the server's own preview, so what the office reads is what the
// commit will do, or every reason it will not. A reason is required, and the reopen is a two-step confirm (the branded-confirm
// rule). Nothing is sent to the customer or the engineer, and nothing in Xero is touched: the preview says so in words.
import { useEffect, useState } from "react";
import { api, ApiError, type ReopenApprovalPlan } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ModalShell } from "../../components/ModalShell";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Button } from "../../components/Button";
import { formatDate } from "../../lib/format";

const REASON_MIN = 10;

function minutesText(m: number | null): string {
  if (m == null) return "time not recorded";
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h > 0 ? `${h}h ${String(r).padStart(2, "0")}m` : `${r}m`;
}

export function ReopenApprovalModal({ jobId, onClose, onReopened }: { jobId: string; onClose: () => void; onReopened: () => void }) {
  const [plan, setPlan] = useState<ReopenApprovalPlan | null>(null);
  const [refusals, setRefusals] = useState<string[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.jobsheets
      .reopenApprovedPreview(jobId)
      .then((p) => live && setPlan(p))
      .catch((err) => {
        if (!live) return;
        const list = err instanceof ApiError && Array.isArray(err.body?.refusals) ? (err.body!.refusals as { message: string }[]).map((r) => r.message) : null;
        if (list) setRefusals(list);
        else setLoadError(friendlyApiError(err, "Could not check whether this job can be reopened. Please try again."));
      });
    return () => {
      live = false;
    };
  }, [jobId]);

  async function reopen() {
    if (busy || reason.trim().length < REASON_MIN) return;
    setBusy(true);
    setError(null);
    try {
      await api.jobsheets.reopenApproved(jobId, reason.trim());
      onReopened();
    } catch (err) {
      const list = err instanceof ApiError && Array.isArray(err.body?.refusals) ? (err.body!.refusals as { message: string }[]).map((r) => r.message) : null;
      if (list) setRefusals(list);
      setError(list ? null : friendlyApiError(err, "Could not reopen the approval. Nothing was changed. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Reopen approval" onClose={() => !busy && onClose()} width="max-w-[560px]" panelClassName="p-6 max-h-[90vh] overflow-y-auto">
      <h2 className="font-heading text-lg text-ele-strong">Reopen approval</h2>
      <p className="mt-2 text-sm text-ele-body">The job goes back to Closed, awaiting release, so it can be corrected and approved again.</p>

      {!plan && !refusals && !loadError && <p className="mt-4 text-sm text-ele-muted">Checking...</p>}
      {loadError && (
        <p role="alert" className="mt-4 text-sm text-ele-danger">
          {loadError}
        </p>
      )}

      {refusals && (
        <div role="alert" className="mt-4 rounded-ele border border-ele-amber-line p-3 text-sm text-ele-body">
          <p className="font-semibold text-ele-strong">This job cannot be reopened.</p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {refusals.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {plan && !refusals && (
        <div className="mt-4 space-y-3 text-sm text-ele-body">
          <div>
            <p className="font-semibold text-ele-strong">What changes</p>
            <ul className="mt-1 list-disc space-y-1 pl-5">
              <li>Status becomes {plan.willBecome}.</li>
              {plan.timesheetEntriesRemoved.length > 0 ? (
                plan.timesheetEntriesRemoved.map((e, i) => (
                  <li key={i}>
                    Hours removed from {e.engineer ?? "an engineer"}'s week of {formatDate(e.weekStarting)}: {e.entryType === "TRAVEL" ? "travel" : "on site"}, {minutesText(e.minutes)}{e.weekStatus === "SUBMITTED" ? " (that week is already submitted: check it before approving it)" : ""}.
                  </li>
                ))
              ) : (
                <li>No hours were written by the approval.</li>
              )}
              {plan.defectsRemoved.map((d, i) => (
                <li key={i}>Fault removed (raised again on approval): {d.description}</li>
              ))}
              {plan.surveyRequestRemoved && <li>The customer's survey request, not yet drafted, is withdrawn.</li>}
              {(plan.followUpRepairLinksCleared ?? []).map((d, i) => (
                <li key={i}>The fault the engineer flagged, &quot;{d}&quot;, is unlinked from its cancelled repair job, so approving again raises a fresh one.</li>
              ))}
              {plan.lastServiced.action === "rewind" && (
                <li>
                  {plan.lastServiced.to
                    ? `The lift's last service date goes back from ${formatDate(plan.lastServiced.from)} to ${formatDate(plan.lastServiced.to)}.`
                    : `The lift's last service date (${formatDate(plan.lastServiced.from)}) is cleared: the lift had none recorded before this approval.`}
                </li>
              )}
              <li>The filed report is {plan.driveReport}.</li>
            </ul>
          </div>
          {plan.leftInPlace.length > 0 && (
            <div>
              <p className="font-semibold text-ele-strong">What stays as it is</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {plan.leftInPlace.map((l, i) => (
                  <li key={i}>{l}</li>
                ))}
              </ul>
            </div>
          )}
          <p>{plan.nothingSent}</p>
          <label className="block">
            <span className="font-semibold text-ele-strong">Why is it being reopened?</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              className="mt-1 w-full rounded-ele border border-ele-purple-accent bg-transparent p-2 text-sm"
              aria-label="Reason for reopening"
            />
          </label>
          {error && (
            <p role="alert" className="text-sm text-ele-danger">
              {error}
            </p>
          )}
        </div>
      )}

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        {plan && !refusals && (
          <ConfirmButton
            onConfirm={() => void reopen()}
            label="Reopen approval"
            confirmLabel="Reopen it now?"
            busy={busy}
            busyLabel="Reopening..."
            disabled={reason.trim().length < REASON_MIN}
            title={reason.trim().length < REASON_MIN ? `Give a reason (at least ${REASON_MIN} characters).` : undefined}
          />
        )}
      </div>
    </ModalShell>
  );
}
